import { defaultLocale, locales, type Locale } from "@/i18n/config";

/**
 * Detect the user's device/browser language and map it to a supported locale.
 *
 * Uses navigator.languages (prioritized) or navigator.language.
 * Works identically on:
 * - Web: browser language preferences
 * - iOS/Android (Capacitor): WebView inherits device system language
 *
 * Returns defaultLocale ("en") if no supported language is found.
 */
export function detectDeviceLanguage(): Locale {
  if (typeof window === "undefined") return defaultLocale;

  const browserLanguages = navigator.languages || [navigator.language];

  for (const browserLang of browserLanguages) {
    // Try exact match first (e.g. "zh-CN", "pt-BR")
    const exactMatch = locales.find((loc) => loc === browserLang);
    if (exactMatch) return exactMatch;

    // Try primary language code (e.g. "en" from "en-GB")
    const langCode = browserLang.split("-")[0].toLowerCase();
    const langMatch = locales.find(
      (loc) => loc === langCode || loc.startsWith(`${langCode}-`)
    );
    if (langMatch) return langMatch;

    // Partial match for complex codes (e.g. "zh" from "zh-CN" → "zh-CN")
    const partialMatch = locales.find((loc) => {
      const locCode = loc.split("-")[0].toLowerCase();
      return locCode === langCode;
    });
    if (partialMatch) return partialMatch;
  }

  return defaultLocale;
}
